<template>
    <div class="content">
        <div class="container-fluid">
            <div class="page-heading row justify-content-between align-items-center py-3">
                <div class="col-lg-4">
                    <h4 class="page-title">{{ $t('installationCreate.title') }}</h4>
                </div>
                <div class="col-auto order-2 d-none d-sm-block">
                    <ol class="breadcrumb m-0 py-2">
                        <li class="breadcrumb-item">
                            <router-link to="/installation-list">{{ $t('installationList.title') }}</router-link>
                        </li>
                        <li class="breadcrumb-item active">Nouvelle installation</li>
                    </ol>
                </div>
            </div>

            <form @submit.prevent="handleSubmit" class="pb-4">
                <div class="installation-layout">
                    <!-- Informations générales -->
                    <div class="general-information">
                        <div class="card mb-0">
                            <div class="card-header pb-0">
                                <h4 class="header-title">{{ $t('installationCreate.generalInfo') }}</h4>
                            </div>
                            <div class="card-body">
                                <div class="row">
                                    <div class="mb-2 col-12">
                                        <label for="install-name" class="form-label">{{ $t('installationCreate.siteName') }}</label>
                                        <input type="text" class="form-control" id="install-name" v-model="installation.name" required maxlength="30" placeholder="Entrez le nom du site" />
                                    </div>
                                    <div class="mb-2 col-12">
                                        <label for="install-client-name" class="form-label">{{ $t('installationCreate.clientName') }}</label>
                                        <input type="text" class="form-control" id="install-client-name" v-model.trim="installation.client_name" required maxlength="30" :placeholder="$t('installationCreate.clientNamePlaceholder')" />
                                    </div>
                                    <div class="mb-2 col-12">
                                        <label for="install-admin-email" class="form-label">{{ $t('installationCreate.adminEmail') }}</label>
                                        <input type="email" class="form-control" id="install-admin-email" v-model="installation.admin_email" maxlength="254" placeholder="Entrez une adresse email" />
                                    </div>
                                    <div class="mb-2">
                                        <label for="install-address" class="form-label">{{ $t('installationCreate.address') }}</label>
                                        <textarea class="form-control" id="install-address" v-model="installation.address" required maxlength="250" placeholder="Entrez une adresse" rows="2"></textarea>
                                    </div>
                                    <div class="mb-2">
                                        <label for="install-country" class="form-label">{{ $t('installationCreate.country') }}</label>
                                        <input type="text" class="form-control" id="install-country" v-model="installation.country" required maxlength="50" placeholder="Entrez un pays" />
                                    </div>
                                    <div class="mb-2">
                                        <label for="install-timezone" class="form-label">{{ $t('installationCreate.timezone') }}</label>
                                        <select class="form-select" id="install-timezone" v-model="installation.timezone" required>
                                            <option value="" disabled>{{ $t('installationCreate.selectTimezone') }}</option>
                                            <option v-for="timezone in userStore.timezones" :key="timezone" :value="timezone">
                                                {{ timezone }}
                                            </option>
                                        </select>
                                    </div>
                                    <div class="mb-2">
                                        <div class="form-check form-switch">
                                            <input class="form-check-input" type="checkbox" id="install-leasing" v-model="installation.is_leasing">
                                            <label class="form-check-label" for="install-leasing">{{ $t('installationCreate.leasing') }}</label>
                                        </div>
                                    </div>
                                    <div class="mb-2">
                                        <div class="form-check form-switch">
                                            <input class="form-check-input" type="checkbox" id="install-hexa-identifier" v-model="installation.hexa_identifier">
                                            <label class="form-check-label" for="install-hexa-identifier">{{ $t('installationCreate.hexaIdentifier') }}</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Photo ou logo -->
                    <div class="photo-or-logo">
                        <div class="card h-100 mb-0">
                            <div class="card-header pb-0">
                                <h4 class="header-title">{{ $t('installationCreate.photoTitle') }}</h4>
                            </div>
                            <div class="card-body photo-card-body">
                                <div v-if="installation.picture" class="photo-preview">
                                    <img :src="installation.picture" :alt="$t('installationCreate.photoAlt')">
                                </div>
                                <button v-else type="button" class="photo-upload-zone" @click="handleFileUpload">
                                    <i class="mdi mdi-image-plus photo-upload-icon"></i>
                                    <span class="fw-semibold">{{ $t('installationCreate.addPhoto') }}</span>
                                    <small>{{ $t('installationCreate.photoHint') }}</small>
                                </button>
                                <input type="file" id="addFileInput" class="d-none" ref="fileInput" accept="image/*" @change="handleFileChange" />

                                <div v-if="installation.picture" class="photo-actions">
                                    <button type="button" class="btn btn-outline-secondary" @click="handleFileUpload">
                                        <i class="mdi mdi-image-edit-outline me-1"></i>{{ $t('installationCreate.replacePhoto') }}
                                    </button>
                                    <button type="button" class="btn btn-outline-danger" :title="$t('installationCreate.deletePhoto')" :aria-label="$t('installationCreate.deletePhoto')" @click="removePicture">
                                        <i class="mdi mdi-delete-outline"></i>
                                    </button>
                                </div>
                                <div v-else class="photo-requirements">
                                    <i class="mdi mdi-information-outline me-1"></i>
                                    {{ $t('installationCreate.photoFormats') }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Configuration par défaut -->
                    <div class="default-configuration">
                        <div class="card mb-0">
                            <div class="card-header pb-0">
                                <div class="d-sm-flex align-items-center justify-content-between">
                                    <h4 class="header-title">{{ $t('installationCreate.defaultConfig') }}</h4>
                                    <div class="d-flex flex-wrap gap-2 mt-2 mt-sm-0">
                                        <button
                                            v-if="hasRs485ReaderFormat"
                                            type="button"
                                            class="btn btn-sm btn-outline-secondary"
                                            data-bs-toggle="modal"
                                            data-bs-target="#cardProfileListModal"
                                        >
                                            {{ $t('componentEditReader.badgeFormats') }}
                                        </button>
                                        <button
                                            type="button"
                                            class="btn btn-sm btn-outline-primary"
                                            :disabled="!canAddReaderFormat"
                                            data-bs-toggle="modal"
                                            data-bs-target="#openReaderFormatModal"
                                            @click="addReaderFormat"
                                        >
                                            {{ $t('componentEditReader.addFormat') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div class="card-body">
                                <div class="mb-3">
                                    <table v-if="installation.default_config.reader_formats.length > 0" class="table table-centered mb-0">
                                        <thead>
                                            <tr>
                                                <th>{{ $t('componentEditReader.readerFormats') }}</th>
                                                <th class="table-action text-end">&nbsp;</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(format, index) in installation.default_config.reader_formats" :key="index">
                                                <td>{{ format.toString() }}</td>
                                                <td class="table-action text-end">
                                                    <button
                                                        type="button"
                                                        class="btn btn-link text-secondary p-0 me-2"
                                                        :title="$t('common.edit')"
                                                        data-bs-toggle="modal"
                                                        data-bs-target="#openReaderFormatModal"
                                                        @click="editReaderFormat(format, index)"
                                                    >
                                                        <i class="mdi mdi-pencil font-18"></i>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        class="btn btn-link text-secondary p-0"
                                                        :title="$t('common.delete')"
                                                        @click="removeReaderFormat(index)"
                                                    >
                                                        <i class="mdi mdi-trash-can font-18"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                    <div v-else class="text-muted">Aucun format de lecteur</div>
                                </div>

                                <div class="row">
                                    <div class="mb-3 col-md-6">
                                        <label for="default-opening-mode" class="form-label">{{ $t('componentEditReader.openingMode') }}</label>
                                        <select id="default-opening-mode" class="form-select" v-model="openingMode">
                                            <option value="0">{{ $t('componentEditReader.openingModes.openClose') }}</option>
                                            <option value="1">{{ $t('componentEditReader.openingModes.timed') }}</option>
                                        </select>
                                    </div>
                                    <div v-if="openingMode === '1'" class="mb-3 col-12">
                                        <TempoWithUnit
                                            :label="$t('installationCreate.openingDelay')"
                                            v-model:value="installation.default_config.opening_delay.value"
                                            v-model:unit="installation.default_config.opening_delay.unit"
                                            :min="1"
                                            :max="255"
                                        />
                                    </div>
                                    <div class="mb-3 col-12">
                                        <label for="default-push-button-input" class="form-label">{{ $t('componentEditReader.pushButton') }}</label>
                                        <select id="default-push-button-input" class="form-select" v-model="installation.default_config.push_button_input_type">
                                            <option value="undefined">{{ $t('common.disabled') }}</option>
                                            <option value="NO">NO</option>
                                            <option value="NC">NF</option>
                                        </select>
                                    </div>
                                    <div class="mb-3 col-12">
                                        <label for="default-door-contact-input" class="form-label">{{ $t('componentEditReader.doorContact') }}</label>
                                        <select id="default-door-contact-input" class="form-select" v-model="installation.default_config.door_contact_input_type">
                                            <option value="undefined">{{ $t('common.disabled') }}</option>
                                            <option value="NO">NO</option>
                                            <option value="NC">NF</option>
                                        </select>
                                    </div>
                                    <div v-if="installation.default_config.door_contact_input_type !== 'undefined'" class="mb-3 col-12">
                                        <TempoWithUnit
                                            :label="$t('installationCreate.blockedDelay')"
                                            v-model:value="installation.default_config.door_blocked_delay.value"
                                            v-model:unit="installation.default_config.door_blocked_delay.unit"
                                            :min="0"
                                            :max="255"
                                            :text-if-zero="$t('common.disabled')"
                                        />
                                    </div>
                                    <div class="mb-3 col-12">
                                        <div class="form-check form-switch">
                                            <input
                                                id="install-auto-update"
                                                type="checkbox"
                                                class="form-check-input"
                                                v-model="installation.components_auto_update"
                                            />
                                            <label class="form-check-label" for="install-auto-update">
                                                {{ $t('installationCreate.autoUpdate') }}
                                            </label>
                                        </div>
                                    </div>
                                    <div v-if="installation.components_auto_update" class="mb-3 col-md-6">
                                        <label for="install-auto-update-time" class="form-label">{{ $t('installationCreate.autoUpdateTime') }}</label>
                                        <input
                                            id="install-auto-update-time"
                                            type="time"
                                            class="form-control"
                                            v-model="automaticUpdateTime"
                                            step="60"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Form actions -->
                <div class="form-actions-sticky d-flex justify-content-center align-items-center">
                    <router-link to="/installation-list" class="btn btn-outline-secondary me-2">{{ $t('common.cancel') }}</router-link>
                    <button type="submit" class="btn btn-primary" :disabled="submitting">{{ $t('installationCreate.create') }}</button>
                </div>
            </form>
        </div>
    </div>
    <ConfirmationModal ref="confirmCreateModal" :title="$t('installationCreate.confirmTitle')" :question="confirmQuestion" type="primary" :button1="$t('common.no')" :button2="$t('common.yes')" :action="createInstallation" />
    <ReaderFormatModal ref="readerFormatModal" @onReaderFormatUpdated="onReaderFormatUpdated" />
    <CardProfileListModal
        :profiles="installation.card_profiles"
        :can-add="canAddCardProfile"
        @add="addCardProfile"
        @edit="editCardProfile"
        @remove="removeCardProfile"
    />
    <CardProfileModal ref="cardProfileModal" @cardProfileSaved="saveCardProfile" />
</template>

<script>
import { computed, reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useUserStore } from '@/stores/userStore';
import { useI18n } from 'vue-i18n';
import { showToast } from '@/services/toast';
import Facility from '@/models/facility';
import { readerTypes } from '@/constants/constants';
import ReaderFormatModal from '@/modals/readerFormatModal.vue';
import TempoWithUnit from '@/components/tempoWithUnit.vue';
import CardProfileListModal from '@/modals/cardProfileListModal.vue';
import CardProfileModal from '@/modals/cardProfileModal.vue';
import ConfirmationModal from '@/modals/confirmationModal.vue';

export default {
    name: 'InstallationCreate',
    components: {
        ConfirmationModal,
        ReaderFormatModal,
        TempoWithUnit,
        CardProfileListModal,
        CardProfileModal,
    },
    setup() {
        const { t } = useI18n();
        const userStore = useUserStore();
        const router = useRouter();
        const fileInput = ref(null);
        const readerFormatModal = ref(null);
        const cardProfileModal = ref(null);
        const confirmCreateModal = ref(null);
        const confirmQuestion = ref('');
        const submitting = ref(false);
        const editedCardProfileIndex = ref(-1);

        const installation = reactive(new Facility());

        const openingMode = computed({
            get: () => installation.default_config.opening_delay.value === 0 ? '0' : '1',
            set: (value) => {
                if(value === '0') {
                    installation.default_config.opening_delay.value = 0;
                } else if(installation.default_config.opening_delay.value === 0) {
                    installation.default_config.opening_delay.value = 5;
                }
            },
        });

        const automaticUpdateTime = computed({
            get: () => {
                const seconds = Number(installation.components_auto_update_time) || 0;
                const hours = Math.floor(seconds / 3600) % 24;
                const minutes = Math.floor((seconds % 3600) / 60);
                return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
            },
            set: (value) => {
                const [hours, minutes] = value.split(':').map(Number);
                installation.components_auto_update_time = hours * 3600 + minutes * 60;
            },
        });

        const sortReaderTypes = (a, b) => {
            const aIsWAuto = a.formatType === 'WAuto';
            const bIsWAuto = b.formatType === 'WAuto';
            if(aIsWAuto && !bIsWAuto) return -1;
            if(!aIsWAuto && bIsWAuto) return 1;
            return a.label.localeCompare(b.label);
        };

        const availableReaderTypes = (editedFormatId = null) => {
            const usedFormatIds = installation.default_config.reader_formats
                .map(format => format.format_id)
                .filter(formatId => formatId !== editedFormatId);

            if(usedFormatIds.length === 0) {
                return [...readerTypes].sort(sortReaderTypes);
            }

            const compatibleIds = usedFormatIds
                .map(formatId => readerTypes.find(type => type.formatId === formatId)?.compatible ?? [])
                .reduce((intersection, compatible) => intersection.filter(id => compatible.includes(id)));

            return readerTypes
                .filter(type => compatibleIds.includes(type.formatId))
                .sort(sortReaderTypes);
        };

        const canAddReaderFormat = computed(() => {
            return installation.default_config.reader_formats.length < 5
                && availableReaderTypes().length > 0;
        });

        const hasRs485ReaderFormat = computed(() => {
            return installation.default_config.reader_formats.some(format => format.is_rs485);
        });

        const availableCardProfileTypes = (editedProfile = null) => {
            if(editedProfile) return [editedProfile.type];

            const existingTypes = installation.card_profiles.map(profile => profile.type);
            let types = ['HAXO', 'MIFARE', 'DESFIRE', 'STID_VCARD'];
            if(existingTypes.includes('HAXO')) {
                types = types.filter(type => !['HAXO', 'DESFIRE'].includes(type));
            }
            if(existingTypes.includes('MIFARE')) {
                types = types.filter(type => type !== 'MIFARE');
            }
            if(existingTypes.includes('DESFIRE')) {
                types = types.filter(type => !['DESFIRE', 'HAXO'].includes(type));
            }
            if(existingTypes.includes('STID_VCARD')) {
                types = types.filter(type => type !== 'STID_VCARD');
            }
            return types;
        };

        const canAddCardProfile = computed(() => {
            return installation.card_profiles.length < 3
                && availableCardProfileTypes().length > 0;
        });

        const getUsedBusAddresses = (editedFormatIndex = -1) => {
            return installation.default_config.reader_formats
                .filter((format, index) => format.is_rs485 && index !== editedFormatIndex)
                .map(format => Number(format.bus_address));
        };

        const addReaderFormat = () => {
            readerFormatModal.value.setFormat(-1, null, availableReaderTypes(), getUsedBusAddresses());
        };

        const editReaderFormat = (format, index) => {
            readerFormatModal.value.setFormat(
                index,
                format,
                availableReaderTypes(format.format_id),
                getUsedBusAddresses(index)
            );
        };

        const removeReaderFormat = (index) => {
            installation.default_config.reader_formats.splice(index, 1);
        };

        const onReaderFormatUpdated = (index, format) => {
            if(index === -1) {
                installation.default_config.reader_formats.push(format);
            } else {
                installation.default_config.reader_formats[index] = format;
            }
        };

        const addCardProfile = () => {
            editedCardProfileIndex.value = -1;
            cardProfileModal.value.setProfile(null, installation.card_profiles);
        };

        const editCardProfile = (profile) => {
            editedCardProfileIndex.value = installation.card_profiles.indexOf(profile);
            cardProfileModal.value.setProfile(profile, installation.card_profiles);
        };

        const saveCardProfile = (profile) => {
            if(editedCardProfileIndex.value === -1) {
                installation.card_profiles.push(profile);
            } else {
                installation.card_profiles[editedCardProfileIndex.value] = profile;
            }
        };

        const removeCardProfile = (profile) => {
            if(!confirm(t('installationCreate.confirmDeleteFormat'))) return;
            const index = installation.card_profiles.indexOf(profile);
            if(index !== -1) installation.card_profiles.splice(index, 1);
        };

        const handleSubmit = async () => {
            if(submitting.value) return;
            submitting.value = true;
            try {
                // An admin email already used by a client attaches the installation to that
                // client (multi-site) : the confirmation then warns the installer about it
                const emailInUse = installation.admin_email
                    && await userStore.isAdminEmailInUse(installation.admin_email);
                confirmQuestion.value = emailInUse
                    ? t('installationCreate.adminEmailInUse')
                    : t('installationCreate.confirmCreate');
                confirmCreateModal.value.show();
            } finally {
                submitting.value = false;
            }
        };

        const createInstallation = async () => {
            if(submitting.value) return;
            submitting.value = true;
            try {
                const createdInstallation = await userStore.createInstallation(installation);
                for(const profile of installation.card_profiles) {
                    await userStore.createCardProfile(createdInstallation.id, profile);
                }
                await router.push('/installation-list');
                if(createdInstallation.pending_validation) {
                    // The administrator must validate the installation before the installer can open it
                    showToast('info', t('installationCreate.createdPending'), { autoClose: 8000 });
                } else {
                    showToast('success', t('installationCreate.created'));
                }
            } catch (e) {
                showToast('error', e);
            } finally {
                submitting.value = false;
            }
        };

        const handleFileChange = (event) => {
            const file = event.target.files[0];
            if (file) {
                if (file.type.startsWith('image/')) {
                    const reader = new FileReader();
                    reader.onload = () => {
                        installation.picture = reader.result;
                    };
                    reader.readAsDataURL(file);
                } else {
                    showToast('error', t('installationCreate.invalidImage'));
                }
            }
        };

        const handleFileUpload = () => {
            document.getElementById('addFileInput').click();
        };

        const removePicture = () => {
            installation.picture = '';
        };

        return {
            userStore,
            installation,
            fileInput,
            readerFormatModal,
            cardProfileModal,
            openingMode,
            automaticUpdateTime,
            canAddReaderFormat,
            hasRs485ReaderFormat,
            canAddCardProfile,
            addReaderFormat,
            editReaderFormat,
            removeReaderFormat,
            onReaderFormatUpdated,
            addCardProfile,
            editCardProfile,
            saveCardProfile,
            removeCardProfile,
            confirmCreateModal,
            confirmQuestion,
            submitting,
            handleSubmit,
            createInstallation,
            handleFileChange,
            handleFileUpload,
            removePicture,
        };
    },
};
</script>

<style scoped>
.installation-layout {
    display: grid;
    gap: 1.5rem;
    grid-template-areas:
        "information photo"
        "configuration photo";
    grid-template-columns: minmax(0, 2fr) minmax(300px, 1fr);
}

.general-information {
    grid-area: information;
}

.default-configuration {
    grid-area: configuration;
}

.photo-or-logo {
    grid-area: photo;
    min-height: 100%;
}

.photo-card-body {
    align-items: stretch;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    min-height: 560px;
}

.photo-preview,
.photo-upload-zone {
    aspect-ratio: 4 / 3;
    border-radius: 6px;
    width: 100%;
}

.photo-preview {
    align-items: center;
    background-color: #f6f7fb;
    border: 1px solid #dee2e6;
    display: flex;
    justify-content: center;
    overflow: hidden;
}

.photo-preview img {
    height: 100%;
    object-fit: contain;
    width: 100%;
}

.photo-upload-zone {
    align-items: center;
    background-color: #f6f7fb;
    border: 1px dashed #a1a9b1;
    color: #6c757d;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 1.5rem;
}

.photo-upload-zone:hover {
    background-color: #eef2f7;
    border-color: var(--ct-primary);
    color: var(--ct-primary);
}

.photo-upload-icon {
    font-size: 2.5rem;
    line-height: 1;
    margin-bottom: 0.75rem;
}

.photo-upload-zone small {
    color: #8a969c;
    margin-top: 0.25rem;
}

.photo-actions {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
}

.photo-requirements {
    color: #8a969c;
    font-size: 0.8rem;
    text-align: center;
}

@media (max-width: 991.98px) {
    .installation-layout {
        grid-template-areas:
            "information"
            "photo"
            "configuration";
        grid-template-columns: minmax(0, 1fr);
    }

    .photo-card-body {
        min-height: 360px;
    }
}
</style>
